import { useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import * as invitesApi from '@/api/invites'
import { useAuth } from '@/auth/AuthContext'
import { useSiteSettings } from '@/siteSettingsContext'

const LOGIN_NAVY = '#0a192f'
const LOGIN_TEAL = '#2a7a76'

export function AcceptInvite() {
  const { token } = useParams()
  const { user, loading: authLoading } = useAuth()
  const { siteTitle, logoText, appVersion } = useSiteSettings()
  const [preview, setPreview] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [done, setDone] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [surname, setSurname] = useState('')
  const [password, setPassword] = useState('')
  const [password2, setPassword2] = useState('')

  const initials = (logoText || 'BK').slice(0, 4).toUpperCase()
  const title = preview?.site_title || siteTitle || 'Bekat KB'
  const versionLabel = appVersion
    ? `v${String(appVersion).replace(/^v/i, '')}`
    : null

  useEffect(() => {
    if (!token) return undefined
    let cancelled = false
    async function load() {
      setLoading(true)
      setError('')
      try {
        const data = await invitesApi.previewInvite(token)
        if (!cancelled) {
          setPreview(data)
          if (data.first_name) setFirstName(data.first_name)
          if (data.surname) setSurname(data.surname)
        }
      } catch (err) {
        if (!cancelled) {
          const detail = err.response?.data?.detail
          setError(
            typeof detail === 'string'
              ? detail
              : 'This invite link is not valid.',
          )
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [token])

  if (!authLoading && user) {
    return <Navigate to="/" replace />
  }

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')
    if (password !== password2) {
      setError('Passwords do not match.')
      return
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.')
      return
    }
    setSubmitting(true)
    try {
      const result = await invitesApi.acceptInvite(token, {
        first_name: firstName.trim(),
        surname: surname.trim(),
        password,
      })
      setDone(result)
    } catch (err) {
      const detail = err.response?.data?.detail
      setError(
        typeof detail === 'string'
          ? detail
          : 'Could not create your account. Try again or ask for a new invite.',
      )
    } finally {
      setSubmitting(false)
    }
  }

  const inputClass =
    'mt-1.5 w-full rounded-lg border border-slate-300 bg-white px-3.5 py-2.5 text-[15px] text-slate-900 outline-none transition focus:border-[#2a7a76] focus:ring-2 focus:ring-[#2a7a76]/20'

  return (
    <div
      className="flex min-h-screen items-center justify-center p-4"
      style={{ backgroundColor: LOGIN_NAVY }}
    >
      <div className="w-full max-w-md rounded-2xl bg-white p-8 shadow-xl">
        <div className="mb-6 flex items-center gap-3">
          <div
            className="flex h-11 w-11 items-center justify-center rounded-lg text-sm font-bold text-white"
            style={{ backgroundColor: LOGIN_TEAL }}
          >
            {initials}
          </div>
          <div>
            <h1 className="text-xl font-semibold text-slate-900">{title}</h1>
            {versionLabel && (
              <p className="text-xs text-slate-500">{versionLabel}</p>
            )}
          </div>
        </div>

        <h2 className="text-lg font-semibold text-slate-900">Accept invitation</h2>

        {loading && <p className="mt-4 text-sm text-slate-500">Loading invite…</p>}

        {!loading && error && !preview && (
          <div className="mt-4 space-y-4">
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </p>
            <Link to="/login" className="text-sm font-medium underline" style={{ color: LOGIN_TEAL }}>
              Go to sign in
            </Link>
          </div>
        )}

        {done && (
          <div className="mt-4 space-y-4">
            <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
              {done.message}
            </p>
            <Link
              to="/login"
              className="inline-flex rounded-lg px-4 py-2.5 text-sm font-semibold text-white"
              style={{ backgroundColor: LOGIN_TEAL }}
            >
              Sign in as {done.email}
            </Link>
          </div>
        )}

        {!loading && preview && !done && (
          <form onSubmit={handleSubmit} className="mt-4 space-y-4">
            <p className="text-sm text-slate-600">
              You are joining as <strong>{preview.email}</strong>
              {preview.role === 'admin' ? ' (admin)' : ''}.
              {preview.customer_names?.length > 0 && (
                <>
                  {' '}
                  Access:{' '}
                  <strong>{preview.customer_names.join(', ')}</strong>.
                </>
              )}
            </p>
            <p className="text-xs text-slate-500">
              Link expires {new Date(preview.expires_at).toLocaleString('en-GB')}.
            </p>
            {error && (
              <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                {error}
              </p>
            )}
            <label className="block text-sm">
              <span className="text-slate-600">First name</span>
              <input
                required
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className={inputClass}
                autoComplete="given-name"
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">Surname</span>
              <input
                required
                value={surname}
                onChange={(e) => setSurname(e.target.value)}
                className={inputClass}
                autoComplete="family-name"
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">Password</span>
              <input
                required
                type="password"
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputClass}
                autoComplete="new-password"
              />
            </label>
            <label className="block text-sm">
              <span className="text-slate-600">Confirm password</span>
              <input
                required
                type="password"
                minLength={8}
                value={password2}
                onChange={(e) => setPassword2(e.target.value)}
                className={inputClass}
                autoComplete="new-password"
              />
            </label>
            <button
              type="submit"
              disabled={submitting}
              className="w-full rounded-lg py-2.5 text-sm font-semibold text-white disabled:opacity-50"
              style={{ backgroundColor: LOGIN_TEAL }}
            >
              {submitting ? 'Creating account…' : 'Create account'}
            </button>
            <p className="text-center text-xs text-slate-500">
              Already have an account?{' '}
              <Link to="/login" className="underline" style={{ color: LOGIN_TEAL }}>
                Sign in
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  )
}
